import type { ReactNode } from "react";
import { formatPercent, type CheckState, type NoisePositionResult, type RoomDesignCheck } from "@sts/calc";
import type { PlanRoom } from "@sts/geometry";
import type { PlanObjectPoint } from "@sts/rules-data";
import { toView, type PlanFit } from "./plan-view.js";
import { round } from "./plan-marks.jsx";

/**
 * How the MCS design check shows on the drawing, in the fit checks' visual
 * language (`plan-object-marks.tsx`): red for a failure, with a short tag in
 * words, so "too small" and "doesn't fit" are both red but never say the same
 * thing — a fit problem is a "!" on the OBJECT; a room whose radiators are too
 * small is a red wash over the ROOM with "Radiators too small" under its name.
 *
 *   pass    — a faint green wash
 *   fail    — a red wash and a tag with the percentage
 *   warn    — an amber wash
 *   unknown — no wash (grey means "not assessed", and the panel says why)
 *
 * All inert: clicks go through to the floor and objects beneath, exactly as
 * the room labels do.
 */

const INERT = { pointerEvents: "none" } as const;

function pathOf(points: readonly PlanObjectPoint[], fit: PlanFit): string {
  return (
    points
      .map((point, i) => {
        const p = toView(point, fit);
        return `${i === 0 ? "M" : "L"} ${round(p.x)},${round(p.y)}`;
      })
      .join(" ") + " Z"
  );
}

export function DesignRoomWash({
  room,
  fit,
  check,
}: {
  readonly room: PlanRoom;
  readonly fit: PlanFit;
  readonly check: RoomDesignCheck;
}): ReactNode {
  if (check.state === "unknown") return null;
  return (
    <g
      className={`floor-plan__design-room floor-plan__design-room--${check.state}`}
      data-testid="design-room-wash"
      data-design-room={`${room.story}:${room.index}`}
      data-state={check.state}
      style={INERT}
      aria-hidden="true"
    >
      {room.outlines.map((outline, i) => (
        <path key={i} d={pathOf(outline, fit)} />
      ))}
    </g>
  );
}

/** The words under a room's name when its radiators are too small: "Radiators too small · 77%". */
export function DesignRoomTag({
  room,
  fit,
  check,
}: {
  readonly room: PlanRoom;
  readonly fit: PlanFit;
  readonly check: RoomDesignCheck;
}): ReactNode {
  if (check.state !== "fail" || check.fraction === undefined) return null;
  const centre = toView(room.centre, fit);
  const text = `Radiators too small · ${formatPercent(check.fraction, { failing: true })}`;
  const w = text.length * 6.1 + 16;
  const y = centre.y + 30;
  return (
    <g className="floor-plan__design-tag" data-testid="design-room-tag" style={INERT} aria-hidden="true">
      <rect x={round(centre.x - w / 2)} y={round(y - 9)} width={round(w)} height={18} rx={9} />
      <text x={round(centre.x)} y={round(y)}>
        {text}
      </text>
    </g>
  );
}

/**
 * A neighbour's window — an MCS 020 a) assessment position — placed on the
 * plan: a marker, a dashed line from the unit it is measured from, and the
 * level there.
 */
export function NoisePositionMark({
  from,
  at,
  fit,
  result,
  label,
  ghost = false,
}: {
  readonly from: PlanObjectPoint;
  readonly at: PlanObjectPoint;
  readonly fit: PlanFit;
  readonly result?: NoisePositionResult;
  readonly label?: string;
  readonly ghost?: boolean;
}): ReactNode {
  const a = toView(from, fit);
  const b = toView(at, fit);
  const state: CheckState = result?.state ?? "unknown";
  const text =
    result?.levelDb !== undefined
      ? `${result.levelDb.toFixed(1)} dB(A)${label ? ` · ${label}` : ""}`
      : label ?? "Neighbour's window";
  const w = Math.min(text.length, 40) * 6.1 + 14;
  return (
    <g
      className={`floor-plan__noise floor-plan__noise--${state}${ghost ? " is-ghost" : ""}`}
      data-testid={ghost ? "noise-position-ghost" : "noise-position"}
      style={INERT}
      aria-hidden="true"
    >
      <line className="floor-plan__noise-line" x1={round(a.x)} y1={round(a.y)} x2={round(b.x)} y2={round(b.y)} />
      <rect className="floor-plan__noise-window" x={round(b.x - 6)} y={round(b.y - 6)} width={12} height={12} rx={2} />
      <g className="floor-plan__noise-tag">
        <rect x={round(b.x + 10)} y={round(b.y - 9)} width={round(w)} height={18} rx={9} />
        <text x={round(b.x + 10 + w / 2)} y={round(b.y)}>
          {text.length > 40 ? `${text.slice(0, 39)}…` : text}
        </text>
      </g>
    </g>
  );
}
